/**
 * The autocue following the presenter's voice. The page cuts what the presenter reads into words; the speech
 * recogniser hears the studio microphone; this matches what it heard against the words ahead of where the
 * presenter was, and says where the presenter is now: the next word to say. It tolerates what speech does to a
 * script — words the recogniser misses or gets wrong, a word ad-libbed, a phrase skipped, an inflection — and does
 * not move for a lone "και" or "το" heard somewhere: a jump needs real words matched in a row, more of them the
 * further it goes. Pure, so the page and the tests share it.
 */

/** A word as the matcher compares it: no accents, no case, a final sigma as a sigma, letters and digits only. */
export function wordKey(word: string): string {
  return word
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/ς/g, 'σ')
    .replace(/[^\p{L}\p{N}]/gu, '');
}

/** One piece of a read as the page draws it: a word (with its place among the autocue's words) or the space after it. */
export interface Piece {
  text: string;
  /** The word's place in `Script.keys`; -1 for spaces and for marks that are not words (a dash, a bullet). */
  at: number;
}

export interface ScriptRead {
  mode: 'on' | 'vo';
  pieces: Piece[];
}

export interface Script {
  /** Every word of the autocue in order, as `wordKey` makes it. */
  keys: string[];
  /** The stories in running order with the place of their first word (a story without words starts where the next one does). */
  stories: Array<{ id: string; start: number }>;
  /** The reads each story shows, cut into pieces. */
  reads: Map<string, ScriptRead[]>;
}

interface ScriptItem {
  id: string;
  kind: string;
  reads: Array<{ mode: 'on' | 'vo'; text: string }>;
}

/** The autocue's words: the ON reads of every story, and the V.O ones when they are shown. */
export function scriptOf(items: ScriptItem[], withVo: boolean): Script {
  const keys: string[] = [];
  const stories: Script['stories'] = [];
  const reads = new Map<string, ScriptRead[]>();
  for (const it of items) {
    if (it.kind === 'separator') continue;
    stories.push({ id: it.id, start: keys.length });
    reads.set(
      it.id,
      it.reads
        .filter((r) => withVo || r.mode === 'on')
        .map((r) => ({
          mode: r.mode,
          pieces: r.text
            .split(/(\s+)/)
            .filter((s) => s !== '')
            .map((text) => {
              const key = /\s/.test(text) ? '' : wordKey(text);
              if (!key) return { text, at: -1 };
              keys.push(key);
              return { text, at: keys.length - 1 };
            }),
        })),
    );
  }
  return { keys, stories, reads };
}

/** The story a word belongs to, as its index in `Script.stories`; -1 before the first. */
export function storyOfWord(script: Script, at: number): number {
  // stories without words share their start with the next one: the word belongs to the last of them
  let k = -1;
  script.stories.forEach((s, i) => {
    if (s.start <= at) k = i;
  });
  return k;
}

/**
 * The same place in a script that changed under it (a story edited while on air): the same word of the same story;
 * if that story is gone, the start of the next one still there; in another bulletin altogether, the top.
 */
export function relocate(from: Script, to: Script, at: number): number {
  const k = storyOfWord(from, at);
  for (let i = Math.max(0, k); i < from.stories.length; i++) {
    const story = from.stories[i]!;
    const there = to.stories.findIndex((s) => s.id === story.id);
    if (there < 0) continue;
    const start = to.stories[there]!.start;
    if (i !== k) return start;
    const end = to.stories[there + 1]?.start ?? to.keys.length;
    return Math.min(start + (at - story.start), end);
  }
  return 0;
}

/** How much two words agree, 0 to 1: the same word, one inflected from the other ("Λευκωσία", "Λευκωσίας"), or one letter off. */
export function similar(a: string, b: string): number {
  if (a === b) return 1;
  if (/\p{N}/u.test(a) || /\p{N}/u.test(b)) return 0;
  const short = Math.min(a.length, b.length);
  const long = Math.max(a.length, b.length);
  if (short >= 4 && (a.startsWith(b) || b.startsWith(a))) return 0.8;
  let p = 0;
  while (p < short && a[p] === b[p]) p++;
  if (short >= 5 && p >= Math.max(4, Math.ceil(long * 0.6))) return 0.7;
  if (short >= 4 && editDistance(a, b) <= (long >= 8 ? 2 : 1)) return 0.7;
  return 0;
}

function editDistance(a: string, b: string): number {
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) row.push(Math.min(prev[j]! + 1, row[j - 1]! + 1, prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1)));
    prev = row;
  }
  return prev[b.length]!;
}

/** A short word ("η", "το", "και") is everywhere, so it counts for little; a number counts in full. */
function weight(key: string): number {
  if (/\p{N}/u.test(key)) return 1;
  return key.length <= 2 ? 0.35 : key.length === 3 ? 0.6 : 1;
}

/** How far back the presenter may be found (a phrase read again), and how far ahead (a phrase skipped). */
export const LOOK_BACK = 8;
export const LOOK_AHEAD = 60;
/** The most recent words heard that are matched; earlier ones were matched before. */
const HEARD_WORDS = 30;
const MISMATCH = -1.2;
/** A word heard that is not in the script (an ad-lib, a word misheard). */
const EXTRA_HEARD = -1;
/** A word of the script not heard (the recogniser missed it, or the presenter skipped it). */
const MISSED = -0.5;

/** The evidence needed to put the presenter at a match that starts at `start` when they were at `from`. */
function needed(start: number, from: number): number {
  if (start >= from - 1 && start <= from + 2) return 1.4; // carrying on: one real word is enough, even inflected differently
  if (start > from) return 3 + 0.03 * (start - from); // ahead: a phrase skipped, at least two real words
  return 4; // back: a phrase read again
}

export interface Match {
  /** The next word to say: one past the last word matched. */
  at: number;
  score: number;
}

/**
 * Where the presenter is, from the words heard since they were at `from`: the best local alignment of the heard
 * words with the script around `from` (a word may be missed, misheard, added), nearest `from` among equals. Null
 * when nothing heard is convincing enough to move.
 */
export function matchHeard(keys: string[], heard: string[], from: number): Match | null {
  const said = heard.filter(Boolean).slice(-HEARD_WORDS);
  if (!said.length || !keys.length) return null;
  const lo = Math.max(0, from - LOOK_BACK);
  const hi = Math.min(keys.length, from + LOOK_AHEAD + 2 * said.length);
  const m = hi - lo;
  if (m <= 0) return null;
  // score and start of the best alignment ending at each (heard word, script word)
  let prev = new Float64Array(m + 1);
  let prevStart = new Int32Array(m + 1);
  let best: { at: number; score: number; start: number; rank: number } | null = null;
  for (let i = 1; i <= said.length; i++) {
    const cur = new Float64Array(m + 1);
    const curStart = new Int32Array(m + 1);
    const h = said[i - 1]!;
    for (let j = 1; j <= m; j++) {
      const key = keys[lo + j - 1]!;
      const sim = similar(h, key);
      const diag = sim > 0 ? 2 * sim * weight(key) : MISMATCH;
      let score = 0;
      let start = lo + j - 1;
      const viaDiag = prev[j - 1]! + diag;
      if (viaDiag > score) {
        score = viaDiag;
        start = prev[j - 1]! > 0 ? prevStart[j - 1]! : lo + j - 1;
      }
      const viaExtra = prev[j]! + EXTRA_HEARD;
      if (viaExtra > score) {
        score = viaExtra;
        start = prevStart[j]!;
      }
      const viaMissed = cur[j - 1]! + MISSED;
      if (viaMissed > score) {
        score = viaMissed;
        start = curStart[j - 1]!;
      }
      cur[j] = score;
      curStart[j] = start;
      // an alignment ends on a word matched; among equals the one nearest where the presenter was
      if (sim > 0 && score > 0 && score >= needed(start, from)) {
        const rank = score - 0.03 * Math.abs(start - from);
        if (!best || rank > best.rank + 1e-9) best = { at: lo + j, score, start, rank };
      }
    }
    prev = cur;
    prevStart = curStart;
  }
  return best ? { at: best.at, score: best.score } : null;
}

/**
 * The presenter's place as the recogniser hears them. A recogniser sends what it heard so far, revised as it hears
 * more, until it is final: the place shown follows every revision, and moves on for good with each final phrase.
 */
export class VoiceFollower {
  private keys: string[];
  private anchor: number;

  constructor(keys: string[], at = 0) {
    this.keys = keys;
    this.anchor = Math.max(0, Math.min(at, keys.length));
  }

  /** The next word to say, as of the last final phrase (or the last move). */
  get at(): number {
    return this.anchor;
  }

  moveTo(at: number): void {
    this.anchor = Math.max(0, Math.min(at, this.keys.length));
  }

  /** A changed script, with the place already carried over to it (`relocate`). */
  reset(keys: string[], at: number): void {
    this.keys = keys;
    this.moveTo(at);
  }

  /** What the recogniser heard since its last final phrase; returns the place to show. */
  hear(text: string, final: boolean): number {
    const m = matchHeard(this.keys, text.split(/\s+/).map(wordKey), this.anchor);
    const at = m ? m.at : this.anchor;
    if (final) this.anchor = at;
    return at;
  }
}
